import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../services/useUserData';
import { apiService, Device } from '../../services/api_url';
import { V } from '../../services/obfyscatedStrings';
import Loader from '../../widgets/Loader/Loader';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import { BackIcon } from '../../assets/images/BackIcon';
import {
    GiftIcon,
    LinkIcon,
    CopyIcon,
    CheckIcon,
    CloseIcon,
    ChartIcon,
    UserIcon,
    CloseModalIcon,
    DeviceIcon,
    ShareIcon
} from '../../assets/images/ReferralIcons';
import './ReferralPage.scss';

interface BonusItem {
    id: string;
    referred_id: string;
    referred_username: string | null;
    available_days: number;
    created_at: string;
}

interface ReferralInfo {
    referral_code: string;
    referral_count: number;
    referral_earnings: number;
}

interface ReferralStats {
    total_referrals: number;
    total_earnings: number;
    total_purchases: number;
    available_bonus_days: number;
    referrals: Array<{
        user_id: string;
        username: string | null;
        created_at: string;
        purchase_amount: number;
        earned: number;
    }>;
}

interface ReferralPageProps {
    onModalStateChange?: (isOpen: boolean) => void;
}

function ReferralPage({ onModalStateChange }: ReferralPageProps) {
    const navigate = useNavigate();
    const { telegramId, loading: userLoading } = useUserData();

    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [copiedType, setCopiedType] = useState<'bot' | 'site' | null>(null);
    const [successDetails, setSuccessDetails] = useState<{
        deviceName: string;
        daysAdded: number;
        newExpiryDate: string;
    } | null>(null);

    const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
    const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
    const [bonusDays, setBonusDays] = useState<number>(0);
    const [devices, setDevices] = useState<Device[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
    const [selectedDays, setSelectedDays] = useState<number>(0);
    const [showDeviceSelection, setShowDeviceSelection] = useState(false);

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }
    }, []);

    useEffect(() => {
        if (!userLoading && telegramId) {
            loadReferralData();
            loadDevices();
        }
    }, [userLoading, telegramId]);

    const loadReferralData = async () => {
        if (!telegramId) return;

        setLoading(true);
        try {
            const response = await apiService.getReferralInfo(telegramId);
            if (response.success) {
                setReferralInfo(response.referral_info);
                setReferralStats({
                    total_referrals: response.stats.total_referrals,
                    total_earnings: response.stats.total_earnings,
                    total_purchases: response.stats.total_purchases,
                    available_bonus_days: response.stats.available_bonus_days,
                    referrals: response.stats.referrals
                });
                setBonusDays(response.bonus_days);
            }
        } catch (error) {
            setError('Не удалось загрузить данные');
        } finally {
            setLoading(false);
        }
    };

    const loadDevices = async () => {
        if (!telegramId) return;

        try {
            const response = await apiService.getUserDevices(telegramId);
            if (response.success) {
                setDevices(response.devices);
            }
        } catch (error) {
        }
    };

    const getBotUsername = (): string => {
        return process.env.REACT_APP_BOT_USERNAME || 'GhostOfTheInternetBot';
    };

    const getSiteUrl = (): string => {
        return process.env.REACT_APP_SITE_URL || 'https://shadownet.nl';
    };

    const getBotLink = (): string => {
        if (referralInfo?.referral_code) {
            return `https://t.me/${getBotUsername()}?start=${referralInfo.referral_code}`;
        }
        return '';
    };

    const getSiteLink = (): string => {
        if (referralInfo?.referral_code) {
            return `${getSiteUrl()}/?ref=${referralInfo.referral_code}`;
        }
        return '';
    };

    const handleCopyBotLink = () => {
        const link = getBotLink();
        if (link) {
            navigator.clipboard.writeText(link);
            setCopiedType('bot');
            setTimeout(() => setCopiedType(null), 2000);
        }
    };

    const handleCopySiteLink = () => {
        const link = getSiteLink();
        if (link) {
            navigator.clipboard.writeText(link);
            setCopiedType('site');
            setTimeout(() => setCopiedType(null), 2000);
        }
    };

    const handleShareBot = async () => {
        const link = getBotLink();
        if (!link) return;

        const shareData = {
            title: `ShadowNet ${V()}`,
            text: `Присоединяйся к ShadowNet ${V()}! Используй мою реферальную ссылку для бонусов:`,
            url: link
        };

        try {
            if (navigator.share && window.innerWidth <= 768) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(link);
                setCopiedType('bot');
                setTimeout(() => setCopiedType(null), 2000);
            }
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                await navigator.clipboard.writeText(link);
                setCopiedType('bot');
                setTimeout(() => setCopiedType(null), 2000);
            }
        }
    };

    const handleShareSite = async () => {
        const link = getSiteLink();
        if (!link) return;

        const shareData = {
            title: `ShadowNet ${V()}`,
            text: `Присоединяйся к ShadowNet ${V()}! Используй мою реферальную ссылку для бонусов:`,
            url: link
        };

        try {
            if (navigator.share && window.innerWidth <= 768) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(link);
                setCopiedType('site');
                setTimeout(() => setCopiedType(null), 2000);
            }
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                await navigator.clipboard.writeText(link);
                setCopiedType('site');
                setTimeout(() => setCopiedType(null), 2000);
            }
        }
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString: string): string => {
        if (!dateString) return 'Дата не указана';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Дата не указана';
        }

        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getDeviceIconType = (device: Device): string => {
        const protocol = device.protocol_name?.toLowerCase() || '';
        if (protocol.includes('wireguard') || protocol.includes('wg')) return 'wireguard';
        if (protocol.includes('vless')) return 'rocket';
        if (protocol.includes('amnezia') || protocol.includes('awg')) return 'shield';
        return 'default';
    };

    const handleApplyBonus = () => {
        if (bonusDays === 0) {
            setError('У вас нет бонусных дней');
            setTimeout(() => setError(null), 3000);
            return;
        }

        if (devices.length === 0) {
            setError('У вас нет устройств для продления');
            setTimeout(() => setError(null), 3000);
            return;
        }

        setSelectedDays(0);
        setSelectedDeviceId(null);
        setShowDeviceSelection(true);
        onModalStateChange?.(true);
    };

    const handleDaySelection = (days: number) => {
        if (days > bonusDays) {
            setError(`У вас только ${bonusDays} бонусных дней`);
            setTimeout(() => setError(null), 2000);
            return;
        }
        setSelectedDays(days);
    };

    const handleCustomDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        if (isNaN(value)) {
            setSelectedDays(0);
            return;
        }
        if (value > bonusDays) {
            setError(`Максимум ${bonusDays} дней`);
            setTimeout(() => setError(null), 2000);
            setSelectedDays(bonusDays);
        } else if (value < 0) {
            setSelectedDays(0);
        } else {
            setSelectedDays(value);
        }
    };

    const handleSelectDevice = async () => {
        if (!telegramId) return;
        if (!selectedDeviceId) {
            setError('Выберите устройство');
            return;
        }
        if (selectedDays <= 0) {
            setError('Выберите количество дней');
            return;
        }

        setProcessing(true);
        setError(null);

        const selectedDevice = devices.find(d => d.id === selectedDeviceId);
        const deviceName = selectedDevice ? formatDeviceName(selectedDevice) : 'устройство';

        try {
            // Отправляем только user_id, device_id и days_to_apply
            const response = await apiService.applyBonusDays({
                user_id: telegramId,
                device_id: selectedDeviceId,
                days_to_apply: selectedDays
                // bonus_ids больше не нужен
            });

            if (response.success) {
                let newExpiryDateFormatted = 'Дата не указана';

                if (response.new_expires_at) {
                    newExpiryDateFormatted = formatDateTime(response.new_expires_at);
                } else {
                    await loadDevices();
                    const updatedDevice = devices.find(d => d.id === selectedDeviceId);
                    if (updatedDevice) {
                        newExpiryDateFormatted = formatDateTime(updatedDevice.expires_at);
                    }
                }

                setSuccessDetails({
                    deviceName: deviceName,
                    daysAdded: response.applied_days || selectedDays,
                    newExpiryDate: newExpiryDateFormatted
                });

                setSuccess(`✅ Устройство "${deviceName}" успешно продлено на ${response.applied_days || selectedDays} дней!`);

                await loadReferralData();
                await loadDevices();

                setShowDeviceSelection(false);
                setSelectedDeviceId(null);
                setSelectedDays(0);
                onModalStateChange?.(false);
            } else {
                setError(response.message || 'Ошибка применения бонусов');
                setTimeout(() => setError(null), 3000);
            }
        } catch (error) {
            setError('Ошибка при применении бонусных дней');
            setTimeout(() => setError(null), 3000);
        } finally {
            setProcessing(false);
        }
    };

    const handleCloseModal = () => {
        setShowDeviceSelection(false);
        setSelectedDeviceId(null);
        setSelectedDays(0);
        onModalStateChange?.(false);
    };

    const handleCloseSuccess = () => {
        setSuccess(null);
        setSuccessDetails(null);
    };

    const getDeviceStatus = (device: Device): { text: string; color: string } => {
        const expiresAt = new Date(device.expires_at);
        const now = new Date();
        const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysLeft < 0) return { text: 'Просрочено', color: 'red' };
        if (daysLeft === 0) return { text: 'Заканчивается сегодня', color: 'yellow' };
        return { text: `Осталось ${daysLeft} дн.`, color: 'green' };
    };

    const formatDeviceName = (device: Device): string => {
        if (device.name) return device.name;
        if (device.protocol_name) return `${device.protocol_name} устройство`;
        return 'Устройство';
    };

    if (userLoading || loading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="referral-page loading">
                    <div className="loader"><Loader /></div>
                </div>
            </div>
        );
    }

    if (!telegramId) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="referral-page">
                    <div className="referral-header">
                        <button className="back-button" onClick={() => navigate(-1)}>
                            <BackIcon />
                        </button>
                        <div className="referral-title">Реферальная программа</div>
                    </div>
                    <div className="error-container">
                        <div className="error-icon">🔒</div>
                        <h2>Необходима авторизация</h2>
                        <p>Для участия в реферальной программе нужно войти в систему</p>
                        <button className="continue-button" onClick={() => navigate('/key')}>
                            Перейти к авторизации
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar />

            <div className="referral-page">
                <div className="referral-header">
                    <button className="back-button" onClick={() => navigate(-1)}>
                        <BackIcon />
                    </button>
                    <div className="referral-title">Реферальная программа</div>
                </div>

                {error && (
                    <div className="error-message">
                        <CloseIcon />
                        <span>{error}</span>
                    </div>
                )}

                {success && !successDetails && (
                    <div className="success-message">
                        <CheckIcon />
                        <span>{success}</span>
                        <button className="success-close-button-mini" onClick={() => setSuccess(null)}>
                            <CloseIcon />
                        </button>
                    </div>
                )}

                {successDetails && (
                    <div className="success-message detailed">
                        <div className="success-icon-large">
                            <CheckIcon />
                        </div>
                        <div className="success-content">
                            <div className="success-title">Продление успешно завершено!</div>
                            <div className="success-details">
                                <div className="detail-row">
                                    <span className="detail-label">Устройство:</span>
                                    <span className="detail-value">{successDetails.deviceName}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Добавлено дней:</span>
                                    <span className="detail-value highlight">+{successDetails.daysAdded}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Новая дата:</span>
                                    <span className="detail-value">{successDetails.newExpiryDate}</span>
                                </div>
                            </div>
                        </div>
                        <button
                            className="success-close-button"
                            onClick={handleCloseSuccess}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                )}

                {/* Бонусная карта */}
                <div className="bonus-card">
                    <div className="bonus-icon">
                        <GiftIcon />
                    </div>
                    <div className="bonus-content">
                        <div className="bonus-label">Ваши бонусные дни</div>
                        <div className="bonus-days-value">{bonusDays}</div>
                        <div className="bonus-hint">
                            <span className="hint-icon">🎁</span>
                            <span>3 дня за каждый месяц подписки друга</span>
                        </div>
                    </div>
                </div>

                {/* Реферальная ссылка */}
                {referralInfo && (
                    <div className="referral-card">
                        <div className="card-header">
                            <div className="card-title">
                                <LinkIcon />
                                <span>Ваша реферальная ссылка</span>
                            </div>
                        </div>

                        {/* Ссылка для Telegram бота */}
                        <div className="link-item">
                            <div className="link-item-label">
                                <span>Рефералка бота</span>
                            </div>
                            <div className="link-container">
                                <div className="link-code-wrapper">
                                    <code className="link-code">
                                        {getBotLink()}
                                    </code>
                                </div>
                                <div className="link-actions">
                                    <button
                                        className={`action-button ${copiedType === 'bot' ? 'copied' : ''}`}
                                        onClick={handleCopyBotLink}
                                        disabled={processing}
                                        title="Копировать"
                                    >
                                        {copiedType === 'bot' ? <CheckIcon /> : <CopyIcon />}
                                    </button>
                                    <button
                                        className="action-button share-button"
                                        onClick={handleShareBot}
                                        disabled={processing}
                                        title="Поделиться"
                                    >
                                        <ShareIcon />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Ссылка для сайта */}
                        <div className="link-item">
                            <div className="link-item-label">
                                <span>Рефералка сайта</span>
                            </div>
                            <div className="link-container">
                                <div className="link-code-wrapper">
                                    <code className="link-code">
                                        {getSiteLink()}
                                    </code>
                                </div>
                                <div className="link-actions">
                                    <button
                                        className={`action-button ${copiedType === 'site' ? 'copied' : ''}`}
                                        onClick={handleCopySiteLink}
                                        disabled={processing}
                                        title="Копировать"
                                    >
                                        {copiedType === 'site' ? <CheckIcon /> : <CopyIcon />}
                                    </button>
                                    <button
                                        className="action-button share-button"
                                        onClick={handleShareSite}
                                        disabled={processing}
                                        title="Поделиться"
                                    >
                                        <ShareIcon />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="link-note">
                            <span className="note-icon">💡</span>
                            Отправьте эту ссылку другу. После его покупки вы получите бонусные дни!
                        </div>
                    </div>
                )}

                {/* Статистика */}
                {referralStats && (
                    <div className="stats-card">
                        <div className="card-header">
                            <div className="card-title">
                                <ChartIcon />
                                <span>Ваша статистика</span>
                            </div>
                        </div>

                        <div className="stats-grid">
                            <div className="stat-item">
                                <div className="stat-value">{referralStats.total_earnings} дн.</div>
                                <div className="stat-label">Всего получено</div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-value">{referralStats.total_referrals}</div>
                                <div className="stat-label">Приглашено друзей</div>
                            </div>
                        </div>

                        {referralStats.referrals.length > 0 && (
                            <div className="referrals-section">
                                <div className="section-subtitle">
                                    <UserIcon />
                                    <span>Приглашенные друзья</span>
                                </div>
                                <div className="referrals-list">
                                    {referralStats.referrals.slice(0, 5).map((ref, index) => (
                                        <div key={index} className="referral-item">
                                            <div className="referral-info">
                                                <span className="referral-name">
                                                    {ref.username ? `@${ref.username}` : ref.user_id.slice(0, 8)}
                                                </span>
                                                <span className="referral-date">
                                                    {formatDate(ref.created_at)}
                                                </span>
                                            </div>
                                            {ref.earned > 0 && (
                                                <div className="referral-bonus">
                                                    <GiftIcon />
                                                    <span>+{ref.earned} дн.</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Кнопка применения бонусов */}
                {bonusDays > 0 && (
                    <button
                        className="apply-bonus-button"
                        onClick={handleApplyBonus}
                        disabled={processing}
                    >
                        <GiftIcon />
                        <span>Применить бонусные дни</span>
                        <span className="button-arrow">→</span>
                    </button>
                )}

                {/* Модальное окно выбора устройства и количества дней */}
                {showDeviceSelection && (
                    <div className="modal-overlay" onClick={handleCloseModal}>
                        <div className="device-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Применить бонусные дни</h3>
                                <button className="close-button" onClick={handleCloseModal}>
                                    <CloseModalIcon />
                                </button>
                            </div>

                            <div className="modal-content">
                                <div className="bonus-info">
                                    <GiftIcon />
                                    <span className="bonus-text">
                        У вас <strong>{bonusDays} бонусных дней</strong>
                    </span>
                                </div>

                                {/* Выбор количества дней */}
                                <div className="days-selection">
                                    <div className="selection-label">Можете выбрать все дни или ввести свое значение:</div>
                                    <div className="days-control">
                                        <button
                                            className={`all-days-button ${selectedDays === bonusDays ? 'active' : ''}`}
                                            onClick={() => handleDaySelection(bonusDays)}
                                        >
                                            Все ({bonusDays})
                                        </button>
                                        <div className="custom-days-input">
                                            <div className="input-wrapper">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={bonusDays}
                                                    value={selectedDays || ''}
                                                    onChange={handleCustomDayChange}
                                                    placeholder="Введите количество"
                                                    className="days-input"
                                                />
                                                <span className="input-suffix">дней</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Выбор устройства */}
                                <div className="devices-list">
                                    {devices.map(device => {
                                        const isSelected = selectedDeviceId === device.id;
                                        const status = getDeviceStatus(device);

                                        return (
                                            <div
                                                key={device.id}
                                                className={`device-option ${isSelected ? 'selected' : ''} ${status.color}`}
                                                onClick={() => setSelectedDeviceId(device.id)}
                                            >
                                                <div className="device-select">
                                                    {isSelected && <CheckIcon />}
                                                </div>
                                                <div className="device-icon">
                                                    <DeviceIcon type={getDeviceIconType(device)} />
                                                </div>
                                                <div className="device-info">
                                                    <div className="device-name">{formatDeviceName(device)}</div>
                                                    <div className="device-details">
                                                        {device.protocol_name && (
                                                            <span className="device-protocol">{device.protocol_name}</span>
                                                        )}
                                                        <span className="device-expires">
                                            до {formatDate(device.expires_at)}
                                        </span>
                                                    </div>
                                                    <div className={`device-status ${status.color}`}>
                                                        {status.text}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <button
                                    className="confirm-button"
                                    onClick={handleSelectDevice}
                                    disabled={!selectedDeviceId || selectedDays === 0 || processing}
                                >
                                    {processing ? 'Применение...' : `Применить ${selectedDays} дней`}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReferralPage;